import { ReactNode } from 'react';
import styled from 'styled-components';
import { getLearnPath, LearnPages } from '../../../../common/src/pages.ts';
import ChevronRightIcon from '../../assets/chevron-right-small.svg?react';
import MicIcon from '../../assets/learn-mic.svg?react';
import { LearnLink } from './learn_navigation.tsx';
import { shortScreen } from './learn_screen_parts.tsx';

// The cards the Learn hub and the class screens are built from (issues #3470, #3719): a rounded
// tinted card with an icon tile, a title, a line on what it is and a status line in the accent
// colour, and the Range Finder's call to action. Short screens (iPhone SE) get tighter cards
// through the same max-height rule the screens use, so a screen of them fits without scrolling.

// The body of a Learn tab screen that sits between the header and the footer tabs (the hub, a
// class screen): fills the space between them on one phone screen without scrolling, with the
// cards and the footnote sharing the slack through a LearnTabSpacer between them
export const LearnTabBody = styled.div`
  flex: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 12px;
  // nothing scrolls when the screen fits, which is the design; a screen that does not fit (a short
  // phone with large text) scrolls rather than clipping its last card out of reach
  overflow-x: hidden;
  overflow-y: auto;

  ${shortScreen} {
    padding: 12px 16px 8px;
  }
`;

export const LearnTabSpacer = styled.div`
  flex: 1 1 0;
`;

// The centred line at the foot of a Learn tab screen
export const LearnTabFootnote = styled.div`
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  color: var(--color-text-darker);
  padding-top: 12px;

  ${shortScreen} {
    padding-top: 6px;
  }
`;

// The cards in a column
export const LearnCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 22px;

  ${shortScreen} {
    gap: 8px;
    margin-top: 12px;
  }
`;

// One card. With `to` it is a link (with a chevron when `chevron` is set) that carries the screen
// it is tapped on as the way back, and `next`, the module it leads into the Range Finder for
// (learn_navigation.tsx); without `to`, it is dimmed and announced as disabled, for something not
// built yet that must not be tappable into a dead end.
export const LearnCard = ({
  to,
  next,
  icon,
  title,
  description,
  status,
  chevron,
  ...dataProps
}: {
  to?: string;
  next?: string;
  icon: ReactNode;
  title: string;
  description: string;
  status: ReactNode;
  chevron?: boolean;
  [dataAttribute: `data-${string}`]: string | undefined;
}) => {
  const content = (
    <>
      <IconTile>{icon}</IconTile>
      <CardText>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        <CardStatus>{status}</CardStatus>
      </CardText>
    </>
  );
  if (!to) {
    return (
      <LockedCard aria-disabled="true" {...dataProps}>
        {content}
      </LockedCard>
    );
  }
  return (
    <CardLink to={to} next={next} {...dataProps}>
      {content}
      {chevron && <Chevron aria-hidden="true" />}
    </CardLink>
  );
};

export const findRangeCallToAction = "Let's find your range!";

export const findRangePillHeight = 40;

// The Range Finder call to action: a soft tinted pill rather than a full-width button, so it reads
// as the one inviting action on the screen without competing with the cards below. The Range
// Finder returns to the screen it was tapped on.
export const FindRangeLink = () => (
  <FindRangePill to={getLearnPath(LearnPages.Range)}>
    <MicIcon aria-hidden="true" />
    <span>{findRangeCallToAction}</span>
    <ChevronRightIcon aria-hidden="true" />
  </FindRangePill>
);

const FindRangePill = styled(LearnLink)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  height: ${findRangePillHeight}px;
  padding: 0 14px 0 12px;
  border-radius: 999px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 17px;
  color: var(--color-tab-selected);
  background-color: color-mix(in srgb, var(--color-tab-selected) 13%, transparent);
  text-decoration: none;
  white-space: nowrap;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const cardStyles = `
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 14px;
  background-color: var(--color-background-search);
  color: var(--color-text);
  text-decoration: none;

  ${shortScreen} {
    gap: 12px;
    padding: 10px 14px;
  }
`;

const CardLink = styled(LearnLink)`
  ${cardStyles}
`;

const LockedCard = styled.div`
  ${cardStyles}
  opacity: 0.45;
`;

const IconTile = styled.div`
  flex: none;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background-color: var(--color-background);
  color: var(--color-tab-selected);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 30px;
    height: 30px;
  }

  ${shortScreen} {
    width: 44px;
    height: 44px;
    border-radius: 10px;

    svg {
      width: 26px;
      height: 26px;
    }
  }
`;

const CardText = styled.div`
  flex: auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 19px;
  line-height: 1.4;

  ${shortScreen} {
    font-size: 17px;
    line-height: 1.3;
  }
`;

const CardDescription = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: var(--color-text-darker);
`;

const CardStatus = styled.div`
  font-size: 13px;
  line-height: 1.4;
  margin-top: 4px;
  color: var(--color-tab-selected);

  ${shortScreen} {
    margin-top: 2px;
  }
`;

const Chevron = styled(ChevronRightIcon)`
  flex: none;
  width: 22px;
  height: 22px;
  margin-right: -4px;
  color: var(--color-text-darker);
`;
